import React from 'react';
import { Form, Button } from '@weezevent/nacre';
import ManualPlacementModal from './ManualModal';
import ModeSelector from './ModeSelector';
import SelectedInventories from './SelectedInventories';
import { useTranslation } from 'react-i18next';
import Waiting from '../Waiting';
import css from '../../styles.module.css';
import { useSegment } from '../../../../../components/tracking/trackingProvider';

export default function SeatsStep({
    form,
    inventories,
    rates = [],
    categories = [],
    loading,
    onChange = () => {},
    previousInventory,
    setPreviousInventory,
    currentInventory,
    selectedZone,
    setSelectedZone,
    setCurrentInventory,
    previousZone,
    setPreviousZone,
    bookingSelectionAuto,
    setBookingSelectionAuto = () => {},
    onConstructAttendees = () => {},
    onChangeAllForm,
    onChangeSteps = () => {},
    fullScreenMode,
    setFullScreenMode = () => {},
    backToSeats,
    step,
    timer,
    unbookBooking = () => {}
}) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const handleClick = React.useCallback(() => {
        if (bookingSelectionAuto) {
            onChangeSteps(step + 1);
            track('ACTION > VALIDATE PLACEMENT', { 'Page Title': 'ADD ATTENDEES' });
        } else {
            setFullScreenMode(true);
        }
    }, [bookingSelectionAuto, onChangeSteps, step, setFullScreenMode]);

    const onSelectInventory = React.useCallback(
        selectedInventories => {
            let selected_inventories = inventories.filter(invt => selectedInventories.includes(invt.pk));
            onChange('inventories', selected_inventories);
        },
        [onChange, inventories]
    );

    const onDeleteInventory = React.useCallback(
        inventory => {
            // Delete inventory and set in into form.inventories
            let inventories = form.inventories;
            let inventoryIndex = inventories.findIndex(s_invt => s_invt.pk === inventory);
            inventories.splice(inventoryIndex, 1);
            onChange('inventories', inventories);
        },
        [form, onChange]
    );

    const onSelectRate = React.useCallback(
        (value, key, rate, currentInventory = null) => {
            // On select inventory, we set inventories and selected rates to create attendee on
            // next step.

            // Get inventory to add it in selected_rates and in form.inventories
            let selectedInventories = form.inventories;
            let inventory = null;
            if (currentInventory) {
                let inventoryTmp = inventories.find(invt => invt.pk === currentInventory);
                if (inventoryTmp) {
                    inventory = inventoryTmp;
                    let inventoryIndex = selectedInventories.findIndex(invt => invt.pk === currentInventory);
                    if (inventoryIndex === -1) {
                        // We do not have inventory , so we have to add it !
                        selectedInventories.push(inventory);
                    }
                }
            }

            let copySelectedRates = form.selected_rates;
            let selected_rate_index = copySelectedRates.findIndex(s_rate => s_rate.rate.pk === rate.pk && s_rate.inventory === inventory);

            if (selected_rate_index < 0) {
                let data = {
                    rate: rate,
                    sessionId: null,
                    inventory: inventory,
                    quantity: 1,
                    price: rate.resolved_price
                };
                data[key] = value;
                copySelectedRates.push(data);
            } else {
                let data = copySelectedRates[selected_rate_index];
                data[key] = value;
                copySelectedRates.splice(selected_rate_index, 1, data);
            }
            onChangeAllForm({ ...form, selected_rates: copySelectedRates, inventories: selectedInventories });
        },
        [form, inventories, onChangeAllForm]
    );

    const onSetRates = React.useCallback(
        (rates, currentInventory) => {
            // On select many rates (used in manual, on select seat), we have to set all rates
            let copySelectedRates = form.selected_rates;

            // Get inventory to add it in selected_rates and in form.inventories
            let selectedInventories = form.inventories;
            let inventory = null;
            if (currentInventory) {
                let inventoryTmp = inventories.find(invt => invt.pk === currentInventory);
                if (inventoryTmp) {
                    inventory = inventoryTmp;
                    let inventoryIndex = selectedInventories.findIndex(invt => invt.pk === currentInventory);
                    if (inventoryIndex === -1) {
                        // We do not have inventory , so we have to add it !
                        selectedInventories.push(inventory);
                    }
                }
            }
            // To avoid duplicates, we use a set to distingish selected rates and process all of them together
            let rateSet = new Set(rates);
            rateSet.forEach(rate => {
                let selected_rate_index = copySelectedRates.findIndex(s_rate => s_rate.rate.pk === rate.pk && s_rate.inventory === inventory);
                let rateCount = rates.filter(r => r.pk === rate.pk).length;

                if (selected_rate_index < 0) {
                    let data = {
                        rate: rate,
                        sessionId: null,
                        inventory: inventory,
                        quantity: rateCount,
                        price: rate.resolved_price
                    };
                    copySelectedRates.push(data);
                } else {
                    let data = copySelectedRates[selected_rate_index];
                    let attendeesWithRateCount = form.attendees.filter(attendee => attendee.rate_id === rate.pk && attendee.booking_uuid?.inventory === inventory.uuid).length;
                    data['quantity'] = attendeesWithRateCount + rateCount;
                    copySelectedRates.splice(selected_rate_index, 1, data);
                }
            });
            onChangeAllForm({ ...form, selected_rates: copySelectedRates, inventories: selectedInventories });
        },
        [form, inventories, onChangeAllForm]
    );

    const onClearInventories = React.useCallback(() => {
        onChange('inventories', []);
    }, [onChange]);

    if (!inventories?.length || !currentInventory || !previousInventory || loading) {
        return <Waiting />;
    }

    return (
        <>
            <Form.Field>
                <ModeSelector
                    mode={bookingSelectionAuto}
                    onOpenInventory={() => track('ACTION > SELECT INVENTORY', { 'Page Title': 'ADD ATTENDEES' })}
                    setMode={automatic => {
                        if (automatic) track('ACTION > SELECT AUTOMATIC PLACEMENT', { 'Page Title': 'ADD ATTENDEES' });
                        else track('ACTION > SELECT PLACE MAP', { 'Page Title': 'ADD ATTENDEES' });
                        setBookingSelectionAuto(automatic);
                    }}
                    onSelectInventory={onSelectInventory}
                    form={form}
                    inventories={inventories}
                />
                <SelectedInventories form={form} bookingSelectionAuto={bookingSelectionAuto} onDeleteInventory={onDeleteInventory} onClearInventories={onClearInventories} />
            </Form.Field>

            <Button
                className={css['progress-buttons']}
                primary
                disabled={bookingSelectionAuto && !form.inventories.length}
                label={t('ticket.common.action.continue')}
                onClick={handleClick}
            />
            <ManualPlacementModal
                unbookBooking={unbookBooking}
                fullScreenMode={fullScreenMode}
                setFullScreenMode={setFullScreenMode}
                inventories={inventories}
                rates={rates}
                previousInventory={previousInventory}
                setPreviousInventory={setPreviousInventory}
                currentInventory={currentInventory}
                setCurrentInventory={setCurrentInventory}
                categories={categories}
                form={form}
                selectedZone={selectedZone}
                setSelectedZone={setSelectedZone}
                previousZone={previousZone}
                setPreviousZone={setPreviousZone}
                onChange={onChange}
                onChangeAllForm={onChangeAllForm}
                onSelectRate={onSelectRate}
                onConstructAttendees={onConstructAttendees}
                onSetRates={onSetRates}
                onChangeSteps={onChangeSteps}
                backToSeats={backToSeats}
                step={step}
                timer={timer}
            />
        </>
    );
}
